const getScannedBaseUrl = () => {
  const configured = (
    process.env.REACT_APP_GS_URL ||
    process.env.REACT_APP_SCAN_SEARCH_API_URL ||
    ''
  ).replace(/\/$/, '');
  const runningLocally =
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

  if (runningLocally) {
    if (configured && /localhost|127\.0\.0\.1/.test(configured)) return configured;
    return 'http://localhost:3000';
  }

  return configured || 'https://ukstaging.getscanned.me';
};

export const extractPrebookingJwt = (raw: string | null | undefined): string => {
  const str = String(raw || '').trim();
  const match = str.match(/([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/);
  return match ? match[1] : str;
};

export const parsePrebookingJwtPayload = (token: string): Record<string, unknown> => {
  try {
    const segment = String(token || '').split('.')[1];
    if (!segment) return {};
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

const prebookingDobStorageKey = (jwt: string, uuid?: string | null) => {
  const payload = parsePrebookingJwtPayload(jwt);
  const id = uuid || (payload.uuid as string) || String(payload.prebooking_id || '');
  return id ? `prebooking_dob_verified_${id}` : null;
};

export const isPrebookingDobVerified = (jwt: string, uuid?: string | null) => {
  if (!jwt) return false;
  if (parsePrebookingJwtPayload(jwt).dob_verified === true) return true;
  const key = prebookingDobStorageKey(jwt, uuid);
  if (!key) return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

export const markPrebookingDobVerified = (jwt: string, uuid?: string | null) => {
  const key = prebookingDobStorageKey(jwt, uuid);
  if (!key) return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // ignore storage errors
  }
};

const prebookingPath = (token: string) => {
  const jwt = extractPrebookingJwt(token);
  return `${getScannedBaseUrl()}/api/v1/prebookings/${encodeURIComponent(jwt)}`;
};

export type PrebookingPayload = {
  uuid?: string;
  status?: string;
  payment_status?: string;
  paid?: boolean;
  first_name?: string;
  surname?: string;
  plan_reference?: string;
  location?: string;
  paid_amount?: number | string;
  aftercare_first_name?: string;
  offer_expires_at?: string;
  offer_expires_on?: string;
  offer_expired?: boolean;
  stripe_link?: string;
};

type ApiResult = {
  ok: boolean;
  status: number;
  body: {
    success?: boolean;
    error?: string;
    error_code?: string;
    data?: PrebookingPayload & {
      already_paid?: boolean;
      redirect_url?: string;
      checkout_url?: string;
    };
    access_token?: string;
  };
};

const networkErrorResult = (error: unknown, context: string): ApiResult => {
  console.error(`${context} failed`, error, getScannedBaseUrl());
  return {
    ok: false,
    status: 0,
    body: {
      success: false,
      error_code: 'network_error',
      error: 'Unable to reach GetScanned. Please try again, or contact us for a new link.',
    },
  };
};

const parseJson = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export const fetchPrebooking = async (token: string): Promise<ApiResult> => {
  try {
    const response = await fetch(prebookingPath(token), {
      headers: { Accept: 'application/json' },
    });
    const body = await parseJson(response);
    return { ok: response.ok, status: response.status, body };
  } catch (error) {
    return networkErrorResult(error, 'fetchPrebooking');
  }
};

export const unlockPrebooking = async (token: string, dob: string): Promise<ApiResult> => {
  try {
    const response = await fetch(`${prebookingPath(token)}/unlock`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ dob }),
    });
    const body = await parseJson(response);
    return { ok: response.ok, status: response.status, body };
  } catch (error) {
    return networkErrorResult(error, 'unlockPrebooking');
  }
};

export const startPrebookingCheckout = async (
  token: string,
  { force = false, landing = 'corevitals' }: { force?: boolean; landing?: 'getscanned' | 'corevitals' } = {}
): Promise<ApiResult> => {
  try {
    const response = await fetch(`${prebookingPath(token)}/checkout`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ force, landing }),
    });
    const body = await parseJson(response);
    return { ok: response.ok, status: response.status, body };
  } catch (error) {
    return networkErrorResult(error, 'startPrebookingCheckout');
  }
};

export const fetchPrebookingPaymentStatus = async ({
  uuid,
  sessionId,
}: {
  uuid?: string | null;
  sessionId?: string | null;
} = {}): Promise<ApiResult> => {
  try {
    const params = new URLSearchParams();
    if (uuid) params.set('uuid', uuid);
    if (sessionId) params.set('session_id', sessionId);
    const response = await fetch(
      `${getScannedBaseUrl()}/api/v1/prebookings/payment_status?${params.toString()}`,
      { headers: { Accept: 'application/json' } }
    );
    const body = await parseJson(response);
    return { ok: response.ok, status: response.status, body };
  } catch (error) {
    return networkErrorResult(error, 'fetchPrebookingPaymentStatus');
  }
};
